export type RealtimeResourceType =
  | 'story'
  | 'character'
  | 'voice'
  | 'cover'
  | 'book-preview'
  | 'chat';

export type RealtimeStatus = 'running' | 'completed' | 'failed';

export interface RealtimeEventEnvelope {
  version: 1;
  eventId: string;
  type: string;
  resourceType: RealtimeResourceType;
  resourceId: string;
  operationId: string;
  revision: number;
  emittedAt: string;
  watchUntil: string;
  status: RealtimeStatus;
  displayName?: string;
  progress?: number;
  message?: string;
  payload?: Record<string, any>;
}

export interface RealtimeResourceWatch {
  resourceType: RealtimeResourceType;
  resourceId: string;
  watchUntil: string;
}

export interface RealtimeRuntimeConfig {
  endpoint: string | null;
  region: string;
  enabledResourceTypes: Partial<Record<RealtimeResourceType, boolean>>;
}

export interface RealtimeProgressSnapshot {
  stage: string;
  message: string;
  progress: number;
  sequence: number;
  metadata: Record<string, any>;
  timestamp: number;
}

export const toRealtimeProgressSnapshot = (
  event: RealtimeEventEnvelope,
): RealtimeProgressSnapshot => {
  const metadata = event.payload || {};
  const terminalStage =
    event.status === 'completed'
      ? 'complete'
      : event.status === 'failed'
        ? 'failed'
        : 'running';
  const emittedAt = Date.parse(event.emittedAt);

  return {
    stage:
      typeof metadata.stage === 'string' && metadata.stage
        ? metadata.stage
        : terminalStage,
    message: event.message || '',
    progress:
      typeof event.progress === 'number'
        ? event.progress
        : event.status === 'completed'
          ? 100
          : 0,
    sequence: event.revision,
    metadata: {
      ...metadata,
      ...(event.operationId ? { operationId: event.operationId } : {}),
    },
    timestamp: Number.isFinite(emittedAt) ? emittedAt : Date.now(),
  };
};

/**
 * AppSync Events only delivers events emitted after a subscription connects.
 * Keep the existing app-level progress socket subscribed while work is active
 * because its subscribe handshake hydrates the latest persisted progress
 * snapshot. Both transports are shared and multiplexed at app scope.
 */
export const shouldUseLegacyProgressBridge = (
  hasActiveResources: boolean,
): boolean => hasActiveResources;

export const toCompletedStoryUpdate = (
  event: RealtimeEventEnvelope,
): Record<string, any> => ({
  ...(event.payload || {}),
  storyId: event.resourceId,
  pending: false,
  progressOnlyComplete: true,
});

export const getRealtimeEventDisplayName = (
  event: RealtimeEventEnvelope,
  fallback?: string,
): string | undefined => {
  const payload = event.payload || {};
  const nestedResource =
    event.resourceType === 'character'
      ? payload.character
      : event.resourceType === 'voice'
        ? payload.voice
        : undefined;
  const candidates = [
    event.displayName,
    payload.displayName,
    payload.storyName,
    payload.chapterTitle,
    payload.rootStoryTitle,
    payload.characterName,
    payload.voiceName,
    payload.bookTitle,
    nestedResource?.characterName,
    nestedResource?.voiceName,
    nestedResource?.name,
    fallback,
  ];
  const value = candidates.find(
    candidate =>
      typeof candidate === 'string' && candidate.trim().length > 0,
  );
  return typeof value === 'string' ? value.trim() : undefined;
};

export const getRealtimeGlobalNoticeMessage = (
  event: RealtimeEventEnvelope,
): string => {
  const displayName = getRealtimeEventDisplayName(event);
  if (event.status === 'failed') {
    if (!displayName) {
      return event.message || 'That update could not be completed.';
    }
    if (event.resourceType === 'story') {
      return event.payload?.action === 'edit'
        ? `“${displayName}” could not be updated.`
        : `“${displayName}” could not be completed.`;
    }
    if (event.resourceType === 'character') {
      return `Character “${displayName}” could not be completed.`;
    }
    if (event.resourceType === 'voice') {
      return `Voice “${displayName}” could not be completed.`;
    }
    if (event.resourceType === 'cover') {
      return `Cover artwork for “${displayName}” could not be completed.`;
    }
    if (event.resourceType === 'book-preview') {
      return `The preview of “${displayName}” could not be completed.`;
    }
    return event.message || `The update for “${displayName}” could not be completed.`;
  }
  if (!displayName) {
    return event.message || 'Your update is ready.';
  }
  if (event.resourceType === 'story') {
    return event.payload?.action === 'edit'
      ? `“${displayName}” has been updated and is ready to play.`
      : `“${displayName}” is ready to play.`;
  }
  if (event.resourceType === 'character') {
    return `Character “${displayName}” is ready.`;
  }
  if (event.resourceType === 'voice') {
    return `Voice “${displayName}” is ready.`;
  }
  if (event.resourceType === 'cover') {
    return `Cover artwork for “${displayName}” is ready.`;
  }
  if (event.resourceType === 'book-preview') {
    return `Your preview of “${displayName}” is ready.`;
  }
  return event.message || `“${displayName}” is ready.`;
};

export const realtimeResourceKey = (
  resourceType: RealtimeResourceType,
  resourceId: string,
): string => `${resourceType}:${resourceId}`;

export const toRealtimeChannelSegment = (value: string): string => {
  const raw = String(value || 'unknown');
  const safe = raw.replace(/[^A-Za-z0-9-]/g, '-').replace(/-+/g, '-');
  let hash = 2166136261;
  for (let index = 0; index < raw.length; index += 1) {
    hash ^= raw.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  const suffix = (hash >>> 0).toString(16).padStart(8, '0');
  const prefix = (safe || 'resource').slice(0, 40);
  return `${prefix}-${suffix}`.slice(0, 50);
};

export const realtimeChannelFor = (
  userId: string,
  resourceType: RealtimeResourceType,
  resourceId: string,
): string =>
  `user/${userId}/${resourceType}/${toRealtimeChannelSegment(resourceId)}`;

const parseTimestamp = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value !== 'string' || !value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getResourceWatchUntil = (
  resource: Record<string, any>,
  options: {
    pending: boolean;
    pendingWindowMs?: number;
    recentWindowMs?: number;
  },
): string | null => {
  const pendingWindowMs = options.pendingWindowMs ?? 15 * 60 * 1000;
  const recentWindowMs = options.recentWindowMs ?? 10 * 60 * 1000;
  const explicitWatchUntil = parseTimestamp(
    resource.realtimeWatchUntil ?? resource.watchUntil,
  );
  if (explicitWatchUntil !== null && explicitWatchUntil > Date.now()) {
    return new Date(explicitWatchUntil).toISOString();
  }
  const timestamps = [
    resource.updatedAt,
    resource.updateDate,
    resource.lastUpdateDate,
    resource.createDate,
    resource.createdAt,
  ]
    .map(parseTimestamp)
    .filter((value): value is number => value !== null);
  const lastChangedAt =
    timestamps.length > 0
      ? Math.max(...timestamps)
      : options.pending
        ? Date.now()
        : 0;
  const watchUntil =
    lastChangedAt + (options.pending ? pendingWindowMs : recentWindowMs);
  return watchUntil > Date.now() ? new Date(watchUntil).toISOString() : null;
};

export const isStoryLive = (story: Record<string, any>): boolean =>
  story.pending === true ||
  story.pendingVideoCreation === true ||
  story.pendingFFmpeg === true ||
  story.pendingLandscape === true ||
  story.pendingSubtitles === true;
